type MetricCardGridColumns = Readonly<{
  xs: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
}>;

type MetricCardGridPlacement = Readonly<{
  gridColumn?: Readonly<{
    sm?: string;
    md?: string;
    lg?: string;
    xl?: string;
  }>;
}>;

const buildRepeatColumns = (count: number): string => `repeat(${String(count)}, minmax(0, 1fr))`;

const normaliseMetricCardCount = (count: number): number =>
  Number.isInteger(count) && count > 0 ? count : 1;

const shouldUseBalancedFiveCardSplit = (cardCount: number, columnCount: number): boolean =>
  cardCount === 5 && (columnCount === 2 || columnCount === 3);

const resolveFiveCardGridColumnCount = (cardCount: number, columnCount: number): number => {
  if (!shouldUseBalancedFiveCardSplit(cardCount, columnCount)) return columnCount;
  return columnCount * 2;
};

const resolveBalancedColumnCount = (cardCount: number, columnCount: number): number => {
  if (cardCount === 4 && columnCount === 3) return 2;
  return resolveFiveCardGridColumnCount(cardCount, columnCount);
};

const resolveFiveCardGridColumn = (cardIndex: number, columnCount: number): string | null => {
  if (columnCount === 2) return cardIndex === 0 ? 'span 4' : 'span 2';
  if (columnCount === 3) return cardIndex < 2 ? 'span 3' : 'span 2';
  return null;
};

export const buildMetricCardGridColumns = (
  cardCount: number,
  maxDesktopColumns = 6,
): MetricCardGridColumns => {
  const resolvedCardCount = normaliseMetricCardCount(cardCount);
  const desktopColumnCount = Math.min(resolvedCardCount, maxDesktopColumns);
  const tabletColumnCount = desktopColumnCount === 4 ? 2 : Math.min(desktopColumnCount, 3);
  const smallColumnCount = Math.min(desktopColumnCount, 2);

  return {
    xs: buildRepeatColumns(1),
    sm: buildRepeatColumns(resolveBalancedColumnCount(resolvedCardCount, smallColumnCount)),
    md: buildRepeatColumns(resolveBalancedColumnCount(resolvedCardCount, tabletColumnCount)),
    lg: buildRepeatColumns(resolveBalancedColumnCount(resolvedCardCount, desktopColumnCount)),
    xl: buildRepeatColumns(resolveBalancedColumnCount(resolvedCardCount, desktopColumnCount)),
  };
};

export const buildMetricCardGridPlacement = (
  cardCount: number,
  cardIndex: number,
  maxDesktopColumns = 6,
): MetricCardGridPlacement => {
  const resolvedCardCount = normaliseMetricCardCount(cardCount);
  if (resolvedCardCount !== 5) return {};

  const desktopColumnCount = Math.min(resolvedCardCount, maxDesktopColumns);
  const tabletColumnCount = desktopColumnCount === 4 ? 2 : Math.min(desktopColumnCount, 3);
  const smallColumnCount = Math.min(desktopColumnCount, 2);
  const smallGridColumn = resolveFiveCardGridColumn(cardIndex, smallColumnCount);
  const tabletGridColumn = resolveFiveCardGridColumn(cardIndex, tabletColumnCount);
  const desktopGridColumn = resolveFiveCardGridColumn(cardIndex, desktopColumnCount);
  const placement: { gridColumn?: { sm?: string; md?: string; lg?: string; xl?: string } } = {};

  if (smallGridColumn !== null) {
    placement.gridColumn = { sm: smallGridColumn };
  }

  if (tabletGridColumn !== null) {
    placement.gridColumn = { ...placement.gridColumn, md: tabletGridColumn };
  } else if (placement.gridColumn) {
    placement.gridColumn = { ...placement.gridColumn, md: 'auto' };
  }

  if (desktopGridColumn !== null) {
    placement.gridColumn = {
      ...placement.gridColumn,
      lg: desktopGridColumn,
      xl: desktopGridColumn,
    };
  } else if (placement.gridColumn) {
    placement.gridColumn = { ...placement.gridColumn, lg: 'auto', xl: 'auto' };
  }

  return placement;
};
